import { formatCurrency, formatDate } from '@if/core/utils'
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { ProductsInsurancePlan } from '@src/data/types/ProductsWorksheetSchema'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CreditApplication, EPaymentMethodMerchants, Merchant } from '../../../../data/types'
import { IFinanceBaseWorksheet } from '../../../../data/types/IFinanceBaseWorksheetSchema'

interface Props {
  worksheet: IFinanceBaseWorksheet
  creditApplication: CreditApplication
  paymentMerchantsByIds?: Record<string, Merchant> | null
  productsPlans: ProductsInsurancePlan[]
  showFundingSection?: boolean
}

const BORROWER_IDS: string[] = [EPaymentMethodMerchants.PayCustomerByBankTransfer, EPaymentMethodMerchants.PayCustomerByCheque]

const FundingSection = ({
  worksheet,
  creditApplication,
  paymentMerchantsByIds,
  productsPlans,
  showFundingSection = true,
}: Props) => {
  const { t } = useTranslation()

  const getMerchantName = useCallback(
    (id: string) => {
      if (BORROWER_IDS.some((n) => n === id) && creditApplication?.applicant.firstName) {
        return `${creditApplication.applicant.firstName} ${creditApplication.applicant.lastName}`
      }
      return paymentMerchantsByIds?.[id]?.name ?? ''
    },
    [paymentMerchantsByIds, creditApplication],
  )

  if (!showFundingSection) {
    return null
  }

  return (
    <Stack direction="column">
      <Typography variant="h6" component="div" gutterBottom>
        {t('worksheet.funding')}
      </Typography>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="subtitle1">{t('worksheetCommon.paymentDate')} :</Typography>
        <Typography variant="subtitle1">{formatDate(worksheet?.deliveryOn)}</Typography>
      </Stack>
      {worksheet?.merchantPayments && (
        <Stack direction="row" justifyContent="space-between" mt={2} spacing={2} width="30rem">
          <TableContainer component={Paper}>
            {!productsPlans ||
              (productsPlans.length === 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('taskManager.merchantName')}</TableCell>
                      <TableCell align="center">{t('worksheetCommon.paymentMethod')}</TableCell>
                      <TableCell align="right">{t('worksheet.paymentAmount')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {worksheet.merchantPayments.map((row) => (
                      <TableRow key={row.merchantId} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell component="th" scope="row">
                          {getMerchantName(row.merchantId)}
                        </TableCell>
                        <TableCell align="center">
                          {t(`EPaymentMethod.${row.paymentMethod}`, { ns: 'enum' })}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ))}
            {productsPlans && productsPlans.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">{t('worksheet.provider')}</TableCell>
                    <TableCell align="center">{t('editCreditApplication.income.other.typeId')}</TableCell>
                    <TableCell align="center">{t('worksheet.term')}</TableCell>
                    <TableCell align="center">{t('worksheet.paymentAmount')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productsPlans.map((row) => (
                    <TableRow
                      key={`${row.provider}${row.type}`}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell align="center" component="th" scope="row">
                        {row.providerName}
                      </TableCell>
                      <TableCell align="center">{t(`EProductsInsurance.${row.type}`, { ns: 'enum' })}</TableCell>
                      <TableCell align="center">
                        {row?.term} {t('worksheet.month')}
                      </TableCell>
                      <TableCell align="center">{formatCurrency(row.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Stack>
      )}
    </Stack>
  )
}

export default FundingSection